import { selectCustomisation } from '@/redux/customisation/selector'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

// Define the base Section interface
interface Section {
  name: string
  type: string
  settings?: Record<string, unknown>
  blocks?: Record<string, unknown>
  blocksOrder?: string[]
  options?: Record<string, unknown>
}

export type UseCustomisation = {
  previewMode: boolean
  sortingSectionId: string
  pageSections: KeyedSection[]
  pagePopups: KeyedSection[]
  pageOptions: Record<string, unknown>
  getLastSectionFromType: (type: string) => Section
  getSectionFromId: (sectionId: string) => Section
}

export interface KeyedSection extends Section {
  key: string
  id: string
}

export type PagePath =
  | 'index'
  | 'product'
  | 'promotions'
  | 'stores'
  | 'checkout'
  | 'user'
  | 'thankyou'
  | 'terms'
  | 'privacy'
  | 'invoice'
  | 'loyalty'
  | 'orders'
  | 'reservations'

interface SectionSettingData {
  pages: Record<
    PagePath,
    {
      sections: string[]
      popups: string[]
      options: Record<string, unknown>
    }
  >
  sections: Record<string, Section>
}

const DEFAULT_SECTION: Section = {
  name: '',
  type: '',
}

// Theme data from the server occasionally lists the same section id more than
// once (e.g. `productDetail`). Deduping here keeps the React key per render
// list unique without forcing every consumer to handle it.
const dedupeById = (list: KeyedSection[]): KeyedSection[] => {
  const seen = new Set<string>()
  return list.filter((section) => {
    if (seen.has(section.id)) return false
    seen.add(section.id)
    return true
  })
}

const useCustomisation = (pagePath?: PagePath): UseCustomisation => {
  const { previewMode, sectionSettingData, sortingSectionId } =
    useSelector(selectCustomisation)

  const page = pagePath ? sectionSettingData.pages[pagePath] : undefined

  // Memoize the section mapping function to prevent recreation on each render
  const mapSectionToKeyedSection = useCallback(
    (sectionId: string): KeyedSection => {
      const section = sectionSettingData.sections[sectionId] || DEFAULT_SECTION
      return {
        ...section,
        key: sectionId,
        id: sectionId,
      }
    },
    [sectionSettingData.sections],
  )

  const pageSections = useMemo(
    () => dedupeById((page?.sections || []).map(mapSectionToKeyedSection)),
    [page?.sections, mapSectionToKeyedSection],
  )

  const pagePopups = useMemo(
    () => dedupeById((page?.popups || []).map(mapSectionToKeyedSection)),
    [page?.popups, mapSectionToKeyedSection],
  )

  const getLastSectionFromType = (type: string): Section => {
    const { sections } = sectionSettingData
    const sectionKey = Object.keys(sections)
      .filter((key) => sections[key]?.type === type)
      .pop()
    return sectionKey && sections?.[sectionKey]
      ? sections[sectionKey]
      : DEFAULT_SECTION
  }

  const getSectionFromId = (sectionId: string): Section =>
    sectionSettingData?.sections?.[sectionId] ?? DEFAULT_SECTION

  return {
    previewMode,
    sortingSectionId,
    pageSections,
    pagePopups,
    pageOptions: page?.options ?? {},
    getLastSectionFromType,
    getSectionFromId,
  }
}

export default useCustomisation
